import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FolderOpen, Plus, Trash2, Moon, Sun, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Collections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCollName, setNewCollName] = useState("");
  const [newCollDesc, setNewCollDesc] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/collections`);
      setCollections(response.data);
    } catch (error) {
      console.error("Error fetching collections:", error);
      toast.error("Failed to fetch collections");
    } finally {
      setLoading(false);
    }
  };

  const createCollection = async () => {
    if (!newCollName.trim()) {
      toast.error("Collection name is required");
      return;
    }

    try {
      await axios.post(`${API}/collections`, {
        name: newCollName.trim(),
        description: newCollDesc.trim() || null
      });
      toast.success("Collection created");
      setNewCollName("");
      setNewCollDesc("");
      setShowCreateDialog(false);
      fetchCollections();
    } catch (error) {
      console.error("Error creating collection:", error);
      toast.error("Failed to create collection");
    }
  };

  const deleteCollection = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this collection?")) {
      return;
    }

    try {
      await axios.delete(`${API}/collections/${id}`);
      toast.success("Collection deleted");
      fetchCollections();
    } catch (error) {
      console.error("Error deleting collection:", error);
      toast.error("Failed to delete collection");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header - Mobile Optimized */}
      <header className="sticky top-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="hover:bg-gray-100 dark:hover:bg-gray-800 h-8 sm:h-10 px-2 sm:px-4"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 truncate" data-testid="collections-title">
                Collections
              </h1>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="create-collection-button" 
                    className="bg-blue-600 hover:bg-blue-700 h-8 sm:h-10 px-2 sm:px-4 text-xs sm:text-sm"
                  >
                    <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                    <span className="hidden sm:inline">New Collection</span>
                    <span className="sm:hidden">New</span>
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="create-collection-dialog">
                  <DialogHeader>
                    <DialogTitle>Create New Collection</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Input
                      placeholder="Collection name"
                      value={newCollName}
                      onChange={(e) => setNewCollName(e.target.value)}
                      data-testid="collection-name-input"
                    />
                    <Textarea
                      placeholder="Description (optional)"
                      value={newCollDesc}
                      onChange={(e) => setNewCollDesc(e.target.value)}
                      data-testid="collection-desc-input"
                    />
                    <Button onClick={createCollection} className="w-full" data-testid="confirm-create-button">
                      Create
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                className="h-8 w-8 sm:h-10 sm:w-10"
              >
                {theme === "light" ? <Moon className="w-4 h-4 sm:w-5 sm:h-5" /> : <Sun className="w-4 h-4 sm:w-5 sm:h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Collections Grid */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]" data-testid="loading-indicator">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-12 sm:py-20" data-testid="no-collections">
            <FolderOpen className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No collections yet</h2>
            <p className="text-sm sm:text-base text-gray-500 mb-4">Create your first collection to organize articles</p>
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-first-collection">
              <Plus className="w-4 h-4 mr-2" />
              Create Collection
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="collections-grid">
            {collections.map((collection, index) => (
              <div
                key={collection.id}
                onClick={() => navigate(`/collections/${collection.id}`)}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md hover:shadow-2xl transform hover:-translate-y-1 cursor-pointer border border-gray-200 dark:border-gray-700 relative group"
                style={{ transition: "all 0.3s ease" }}
                data-testid={`collection-card-${index}`}
              >
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <FolderOpen className="w-10 h-10 sm:w-12 sm:h-12 text-blue-600 dark:text-blue-400" />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                    onClick={(e) => deleteCollection(collection.id, e)}
                    data-testid={`delete-collection-${index}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                  {collection.name}
                </h3>
                {collection.description && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 line-clamp-2">
                    {collection.description}
                  </p>
                )}
                <div className="text-xs sm:text-sm text-gray-500">
                  {collection.article_count} {collection.article_count === 1 ? 'article' : 'articles'}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Collections;